"use client";

import { useEffect, useRef } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

interface BlogMathBlockProps {
  /** Raw LaTeX string — delimiters ($$, \[, \]) are stripped automatically */
  tex: string;
  /** true = display (block) math, false = inline math */
  displayMode?: boolean;
}

/**
 * Renders a LaTeX math expression using KaTeX.
 * Used by the blog markdown renderer to replace the old manual regex-strip approach.
 */
export function BlogMathBlock({ tex, displayMode = true }: BlogMathBlockProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    // Strip outer delimiters: $$...$$  or  \[...\]
    let clean = tex.trim();
    if (clean.startsWith("$$") && clean.endsWith("$$")) {
      clean = clean.slice(2, -2).trim();
    } else if (clean.startsWith("\\[") && clean.endsWith("\\]")) {
      clean = clean.slice(2, -2).trim();
    }

    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (typeof args[0] === "string" && args[0].includes("No character metrics for")) {
        return;
      }
      originalWarn.apply(console, args);
    };

    try {
      katex.render(clean, ref.current, {
        displayMode,
        throwOnError: false,
        strict: false,
        trust: false,
        output: "html",
      });
    } catch {
      // Graceful fallback: show raw LaTeX
      ref.current.textContent = tex;
    } finally {
      console.warn = originalWarn;
    }
  }, [tex, displayMode]);

  if (displayMode) {
    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className="blog-math-block"
        style={{
          margin: "1.5rem 0",
          padding: "1.25rem 1.5rem",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-md)",
          overflowX: "auto",
          textAlign: "center",
          fontSize: "var(--text-lg)",
        }}
      />
    );
  }

  // Inline mode — render as a <span>
  return (
    <span
      ref={ref as React.RefObject<HTMLSpanElement>}
      className="blog-math-inline"
      style={{ display: "inline", verticalAlign: "middle" }}
    />
  );
}

/** Convenience wrapper for inline math: $...$ */
export function BlogInlineMath({ tex }: { tex: string }) {
  return <BlogMathBlock tex={tex} displayMode={false} />;
}
